import { type AnnouncementActionType, type AnnouncementCommand } from './vocabulary';

/** The command takes no params; any stored params are dropped. */
type NoParams = undefined;

/**
 * Every command's params, keyed by command. A command left out fails to compile,
 * so each new command has to declare its params (or `NoParams`).
 */
type AnnouncementParamsByCommandFor<T extends Record<AnnouncementCommand, unknown>> =
  Exclude<keyof T, AnnouncementCommand> extends never ? T : never;

export type AnnouncementParamsByCommand = AnnouncementParamsByCommandFor<{
  openAgent: { prompt?: string };
  openAgentModelPicker: { modelName?: string };
  openAssetPicker: NoParams;
  openBlankCanvas: NoParams;
  openCanvasAgent: { prompt?: string };
  openInCanvas: NoParams;
  selectAgentModel: { modelName: string };
}>;

type AnnouncementCommandActionByCommand = {
  [C in AnnouncementCommand]: [AnnouncementParamsByCommand[C]] extends [NoParams]
    ? {
        command: C;
        label: string;
        type: AnnouncementActionType;
      }
    : {
        command: C;
        label: string;
        params: AnnouncementParamsByCommand[C];
        type: AnnouncementActionType;
      };
};

export type AnnouncementCommandAction = AnnouncementCommandActionByCommand[AnnouncementCommand];

type AnnouncementCommandParamsParser = {
  [C in AnnouncementCommand]: (raw: unknown) => AnnouncementParamsByCommand[C] | null;
};

const parseNoParams = (_raw: unknown): NoParams => undefined;

const parseSelectAgentModelParams = (raw: unknown): AnnouncementParamsByCommand['selectAgentModel'] | null => {
  if (typeof raw !== 'object' || raw === null || !('modelName' in raw)) {
    return null;
  }

  const { modelName } = raw;

  if (typeof modelName !== 'string' || modelName.length === 0) {
    return null;
  }

  return { modelName };
};

const readTrimmedPrompt = (raw: unknown): string | undefined => {
  if (typeof raw !== 'object' || raw === null || !('prompt' in raw)) {
    return undefined;
  }

  const { prompt } = raw;

  if (typeof prompt !== 'string') {
    return undefined;
  }

  const trimmed = prompt.trim();

  return trimmed.length > 0 ? trimmed : undefined;
};

/** `prompt` is optional, so a missing or blank one opens the agent without sending. */
const parseOptionalPromptParams = (raw: unknown): { prompt?: string } => {
  const prompt = readTrimmedPrompt(raw);

  return prompt ? { prompt } : {};
};

/** `modelName` is optional, so missing or invalid params open the picker without a preselection. */
const parseOpenAgentModelPickerParams = (raw: unknown): AnnouncementParamsByCommand['openAgentModelPicker'] => {
  if (typeof raw !== 'object' || raw === null || !('modelName' in raw)) {
    return {};
  }

  const { modelName } = raw;

  if (typeof modelName !== 'string' || modelName.length === 0) {
    return {};
  }

  return { modelName };
};

export const ANNOUNCEMENT_COMMAND_PARAMS: AnnouncementCommandParamsParser = {
  openAgent: parseOptionalPromptParams,
  openAgentModelPicker: parseOpenAgentModelPickerParams,
  openAssetPicker: parseNoParams,
  openBlankCanvas: parseNoParams,
  openCanvasAgent: parseOptionalPromptParams,
  openInCanvas: parseNoParams,
  selectAgentModel: parseSelectAgentModelParams,
};

export const parseAnnouncementCommandParams = <C extends AnnouncementCommand>(
  command: C,
  raw: unknown,
): AnnouncementParamsByCommand[C] | null => ANNOUNCEMENT_COMMAND_PARAMS[command](raw);

const announcementCommandActionWithoutParams =
  <C extends AnnouncementCommand>(command: C) =>
  (label: string, type: AnnouncementActionType, raw: unknown) => {
    if (parseAnnouncementCommandParams(command, raw) === null) {
      return null;
    }

    return { command, label, type };
  };

const announcementCommandActionWithParams =
  <C extends AnnouncementCommand>(command: C) =>
  (label: string, type: AnnouncementActionType, raw: unknown) => {
    const params = parseAnnouncementCommandParams(command, raw);

    if (params === null || params === undefined) {
      return null;
    }

    return { command, label, params, type };
  };

export const ANNOUNCEMENT_COMMAND_ACTION_BUILDERS = {
  openAgent: announcementCommandActionWithParams('openAgent'),
  openAgentModelPicker: announcementCommandActionWithParams('openAgentModelPicker'),
  openAssetPicker: announcementCommandActionWithoutParams('openAssetPicker'),
  openBlankCanvas: announcementCommandActionWithoutParams('openBlankCanvas'),
  openCanvasAgent: announcementCommandActionWithParams('openCanvasAgent'),
  openInCanvas: announcementCommandActionWithoutParams('openInCanvas'),
  selectAgentModel: announcementCommandActionWithParams('selectAgentModel'),
} satisfies {
  [C in AnnouncementCommand]: (
    label: string,
    type: AnnouncementActionType,
    raw: unknown,
  ) => AnnouncementCommandActionByCommand[C] | null;
};
